import React, { ChangeEvent, Dispatch, Fragment, SetStateAction, useRef, useState } from 'react';
import clsx from 'clsx';
import { Listbox, Transition } from '@headlessui/react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid';
import isEmpty from 'lodash/isEmpty';
import { ScrollToFormikError } from '../elements/ScrollToFormikError';
import { FormikErrors } from 'formik';
import { motion } from 'framer-motion';
import Button from './Button';

export interface ISelectDropdownItem {
  id?: string;
  name: string;
  value: string | number | boolean | null;
  description?: string;
}

export interface ISelectDropdownProps {
  selectInputClassName?: string;
  label?: string;
  listSelectedValue?: number | string | boolean | null;
  showAvatar?: boolean;
  search?: boolean;
  onChange: Dispatch<SetStateAction<ISelectDropdownItem | null>> | any;
  onBlur?: (e: React.FocusEvent<any, Element>) => void | null | any;
  list?: Array<ISelectDropdownItem>;
  grouped?: boolean;
  groupedList?: Record<string, Array<ISelectDropdownItem>>;
  placeholder?: string;
  description?: JSX.Element | React.ReactElement | string | number | any;
  name?: string;
  error?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  required?: boolean;
  helperText?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
  disabled?: boolean;
  sort?: boolean;
  emptyListMessage?: string;
  searchPlaceHolder?: string;
  wrapperClasses?: string;
  editable?: boolean;
  IconClass?: string;
  prepend?: React.ReactNode;
  classes?: string;
  labelClasses?: string;
  modalActionName?: string;
  openModalAction?: () => void;
  appendLabelIcon?: JSX.Element;
  showFullDescription?: boolean;
}

function SelectDropdown<T>({
  selectInputClassName,
  label,
  listSelectedValue,
  onChange,
  list = [],
  description,
  name,
  error,
  disabled = false,
  required = false,
  helperText,
  onBlur,
  sort = false,
  searchPlaceHolder = 'Search ...',
  wrapperClasses,
  editable = true,
  placeholder,
  emptyListMessage,
  search,
  IconClass,
  prepend,
  classes,
  labelClasses,
  openModalAction,
  modalActionName,
  appendLabelIcon,
  showFullDescription = false,
}: ISelectDropdownProps): JSX.Element {
  const [searchValue, setSearchValue] = useState<string>('');
  const [showDropdown, setShowDropdown] = useState<boolean>(false);

  const fieldRef = useRef<HTMLDivElement>(null);

  const [searchResult, setSearchResult] = useState<Array<ISelectDropdownItem>>([]);
  const searchedList = isEmpty(searchResult) && searchValue.length < 1 ? list : searchResult;

  const onSearchChange = (evt: ChangeEvent<HTMLInputElement> | undefined) => {
    const val = evt?.target.value ?? '';

    setSearchValue(val);
    const srchList: Array<ISelectDropdownItem> = [];

    list.forEach((listItem) => {
      if (listItem.name?.toString().toLowerCase().includes(val.toLowerCase())) srchList.push(listItem);
    });
    setSearchResult(srchList);
  };

  const getListItemDisplayName = () => {
    return list.find((listItem: ISelectDropdownItem) => listItem.value === listSelectedValue)?.name;
  };  

  return (
    <Listbox
      ref={fieldRef}
      value={
        list.find((listItem: ISelectDropdownItem) => listItem.value === listSelectedValue)?.value
      }
      onChange={onChange}
      disabled={disabled}
      as={'div'}
      className={clsx({ ['animate-wiggle']: error && required }, wrapperClasses)}>
      {({ open }) => (
        <>
          {/* Label */}
          {label && (
            <Listbox.Label
              className={clsx('block text-sm font-medium text-gray-700 dark:text-darkBg-mainText sm:whitespace-nowrap ', {
                ['!text-danger-main']: error && required
              }, labelClasses)}>
              {label}
              {appendLabelIcon && appendLabelIcon}
              <span className="!text-danger-main">{required && '*'}</span>
              {description && (
                <p
                  className={clsx('text-xs text-gray-400 dark:text-darkBg-subText italic', {
                    ['text-danger-border']: error && required
                  })}>
                  {description}
                </p>
              )}
            </Listbox.Label>
          )}

          <div className={clsx(`mt-1 relative`, classes)}>
            {/* Input Field */}
            <div>
             
              <Listbox.Button
                name={name}
                className={clsx(
                  'relative w-full bg-white dark:bg-darkBg border border-gray-300 dark:border-darkBg-border shadow-sm pr-10 py-2 text-left cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-main focus:border-primary-border sm:text-sm rounded-full',
                  { ['bg-red-200 !border-danger-main focus:ring-danger-main focus:border-danger-border']: error && required }, selectInputClassName)}
                onBlur={!open ? onBlur : (e: any) => null}>

                {prepend && (
                  <div
                    className={clsx('absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none w-12 sm:w-15 h-full text-[#004E60]',)}>
                    {prepend}
                  </div>
                )}

                {getListItemDisplayName() ? (
                  <span className={clsx("block truncate dark:text-white", prepend ? 'ml-10' : 'ml-3')}>
                    {getListItemDisplayName()}
                  </span>
                ) : (
                  <div className='flex'>
                    <span className={clsx("text-gray-400 dark:text-darkBg-subText text-sm", prepend ? 'pl-12' : 'pl-3')}>
                      {placeholder ? placeholder : 'Select Option'}
                    </span>
                  </div>
                )}

                {!disabled && (
                  <span className="ml-3 absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none border-none">
                    {open ? (
                      <ChevronUpIcon
                        className={clsx('h-5 w-5 text-gray-400', IconClass)}
                        aria-hidden="true"
                      />
                    ) : (
                      <ChevronDownIcon
                        className={clsx('h-5 w-5 text-gray-400 dark:text-darkBg-subText', IconClass)}
                        aria-hidden="false"
                      />
                    )}
                  </span>
                )}
              </Listbox.Button>


            </div>

            <Transition
              show={open}
              as={Fragment}
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Listbox.Options className="absolute z-40 mt-1 w-full bg-white dark:bg-darkBg shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {isEmpty(list) ? (
                  // List Empty
                  <div className="w-full flex justify-center items-center h-20 flex-col">
                    <h4 className="font-medium !text-red-400">No Option&#40;s&#41; Available!</h4>
                    <small className="text-xs text-gray-500 dark:text-darkBg-subText">
                      {emptyListMessage ? emptyListMessage : 'Refresh to try again'}
                    </small>
                  </div>
                ) : (
                  <>
                    {/* Search */}
                    {(search || list.length > 4) && (
                      <div className="px-2">
                        <div className="py-1 border-b-2 border-gray-200 dark:border-darkBg-border">
                          <div className="absolute top-5 left-5  flex items-center pointer-events-none">
                            <span className="sm:text-sm">
                              <MagnifyingGlassIcon className="h-5 w-5 stroke-gray-400 dark:stroke-darkBg-subText" />
                            </span>
                          </div>

                          <input
                            type="search"
                            name="search"
                            id="search"
                            className="focus:ring-primary-main rounded-full outline-0 focus:border-primary-main block w-full pl-10 text-md sm:text-sm bg-white dark:bg-darkBg dark:text-darkBg-mainText"
                            placeholder={searchPlaceHolder}
                            value={searchValue}
                            onChange={(e) => onSearchChange(e)}
                          />
                        </div>
                      </div>
                    )}
                    {openModalAction && <div className='flex items-center justify-center p-2'>
                      <Button
                        type="button"
                        label={modalActionName}
                        onClick={openModalAction}
                        bgColor='primary'
                        className='!rounded-none !h-[10px]'
                        labelClasses='text-xs'
                        fullWidth
                      />
                    </div>}

                    {/* List */}
                    {searchedList && <div className={`px-1 pb-2 ${searchedList.length > 4 ? 'overflow-y-scroll' : 'overflow-y-auto'} max-h-[250px]`}>
                      <>
                        {(sort ? searchedList.sort() : searchedList).map((listItem, key) => (
                          <Listbox.Option
                            key={key}
                            className={({ active, selected }) =>
                              clsx(
                                active ? 'bg-primary-surface dark:bg-primary-surface dark:text-black' : 'text-gray-900 dark:text-darkBg-mainText',
                                'cursor-default select-none relative py-2 pl-3 pr-0 text-sm hover:cursor-pointer',
                                { ['bg-primary-main']: selected }
                              )
                            }
                            value={listItem.value}>
                            {({ selected }) => (
                              <div className="flex items-center gap-x-1">
                                <div className="gap-1">
                                  <span
                                    className={clsx(
                                      selected ? 'font-semibold' : 'font-normal',
                                      'ml-3 block truncate text-sm w-fit'
                                    )}>
                                    {listItem.name}
                                  </span>
                                  {listItem.description && (
                                    <span className={clsx('ml-3 text-xs text-gray-400 dark:text-darkBg-subText italic')}>
                                      {listItem.description.length > 50 && !showFullDescription ? (listItem.description).slice(0, 50) + '...' : listItem.description}
                                    </span>
                                  )}
                                </div>


                                {selected ? (
                                  <span
                                    className={clsx(
                                      selected ? 'text-white' : 'text-gray-900'
                                      // 'absolute inset-y-0 right-0 flex items-center pr-1'
                                    )}>
                                    <CheckIcon className="h-5 w-5 " aria-hidden="true" />
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </Listbox.Option>
                        ))}
                      </>
                    </div>}
                    {searchedList.length < 1 && <div className='text-lg font-semibold text-center text-gray-300 dark:text-gray-500 py-4'>Not found</div>}
                  </>
                )}
              </Listbox.Options>
            </Transition>
          </div>
          
          {/* Helper Text */}
          <div className={clsx('text-xs text-gray-400 dark:text-darkBg-subText', { ['!text-danger-main']: error })}>
            {helperText && (
              <motion.p
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-2 text-sm text-red-600 flex items-center font-medium"
              >
                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {helperText && typeof helperText === 'string' ? helperText : undefined}
              </motion.p>
            )}
          </div>
          <ScrollToFormikError fieldName={name ?? ''} fieldRef={fieldRef} />
        </>
      )}
    </Listbox>
  );
}

export default SelectDropdown;
